import React, { useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../config/api';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import BookingConfirmationModal from './BookingConfirmationModal';
import { useHotelTitle } from '../hooks/useHotelTitle';

interface AmenitiesBookingModalProps {
  show: boolean;
  onHide: () => void;
  amenity: any;
  hotelId: string;
  guestId: string;
  onBookingSuccess: (booking: any) => void;
}

function getDayOfWeek(dateStr: string) {
  // 0=Sunday, 6=Saturday
  return new Date(dateStr).getDay();
}

const AmenitiesBookingModal: React.FC<AmenitiesBookingModalProps> = ({ show, onHide, amenity, hotelId, guestId, onBookingSuccess }) => {
  const { hotelName } = useHotelTitle('Amenities');
  const [slots, setSlots] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<any>(null);
  const [specialRequest, setSpecialRequest] = useState('');
  const [booking, setBooking] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [amenitiesPrepaidRequired, setAmenitiesPrepaidRequired] = useState(false);
  const [paymentsConfigLoaded, setPaymentsConfigLoaded] = useState(false);

  useEffect(() => {
    if (show && amenity && hotelId) {
      setLoading(true);
      setError('');
      setSlots([]);
      setSelectedSlot(null);
      setSpecialRequest('');
      setSuccess(false);
      setSuccessMsg('');
      setSelectedDate('');
      fetch(API_ENDPOINTS.AMENITIES_SLOTS(hotelId, amenity.id))
        .then(res => res.json())
        .then(data => {
          setSlots(Array.isArray(data) ? data : []);
          setLoading(false);
        })
        .catch(() => {
          setError('Failed to load slots. Please try again.');
          setLoading(false);
        });
    }
  }, [show, amenity, hotelId]);

  useEffect(() => {
    if (!show || !hotelId) return;
    fetch(API_ENDPOINTS.GUEST_RAZORPAY_CONFIG(hotelId))
      .then((r) => r.json())
      .then((d) => setAmenitiesPrepaidRequired(!!d.amenities_prepaid_required))
      .catch(() => setAmenitiesPrepaidRequired(false))
      .finally(() => setPaymentsConfigLoaded(true));
  }, [show, hotelId]);

  // Filter slots by selected date's day of week
  let filteredSlots: any[] = [];
  if (selectedDate && slots.length > 0) {
    const day = getDayOfWeek(selectedDate);
    filteredSlots = slots.filter(slot =>
      slot.day_of_week === null || slot.day_of_week === '' || slot.day_of_week === undefined || String(slot.day_of_week) === String(day)
    );
  }

  const loadRazorpayScript = (): Promise<void> =>
    new Promise((resolve, reject) => {
      if (typeof window !== 'undefined' && window.Razorpay) {
        resolve();
        return;
      }
      const existing = document.getElementById('razorpay-checkout-js');
      if (existing) {
        const t = window.setInterval(() => {
          if (window.Razorpay) {
            window.clearInterval(t);
            resolve();
          }
        }, 50);
        window.setTimeout(() => {
          window.clearInterval(t);
          if (!window.Razorpay) reject(new Error('Razorpay load timeout'));
        }, 15000);
        return;
      }
      const s = document.createElement('script');
      s.id = 'razorpay-checkout-js';
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Could not load payment script'));
      document.body.appendChild(s);
    });

  const submitAmenityBooking = async (razorpay?: {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
  }) => {
    const res = await fetch(API_ENDPOINTS.AMENITIES_BOOK(hotelId, amenity.id), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        slot_id: selectedSlot.id,
        guest_id: guestId,
        booking_date: selectedDate,
        special_request: specialRequest,
        ...(razorpay || {}),
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      const msg =
        data.message ||
        (data.messages && (typeof data.messages === 'object' ? Object.values(data.messages).join(' ') : data.messages)) ||
        'Booking failed.';
      throw new Error(msg);
    }
    setSuccess(true);
    setSuccessMsg('Booking successful! We look forward to welcoming you.');
    onBookingSuccess(data);
    setTimeout(() => {
      onHide();
    }, 1500);
  };

  const handleBook = () => {
    setShowConfirm(true);
  };

  const handleConfirmBooking = async () => {
    setShowConfirm(false);
    setBooking(true);
    setError('');
    try {
      if (amenitiesPrepaidRequired) {
        const cr = await fetch(API_ENDPOINTS.GUEST_RAZORPAY_CREATE_AMENITY_ORDER(hotelId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amenity_id: amenity.id,
            slot_id: selectedSlot.id,
            guest_id: guestId,
            booking_date: selectedDate,
          }),
        });
        const cd = await cr.json();
        if (cr.status === 503 || cd.status === 'disabled') {
          setError(cd.message || 'Online payment is not available. Please contact the front desk.');
          setBooking(false);
          return;
        }
        if (!cr.ok || cd.status !== 'success' || !cd.key_id || !cd.razorpay_order_id) {
          setError(cd.message || (cd.messages && cd.messages.error) || 'Could not start payment.');
          setBooking(false);
          return;
        }
        await loadRazorpayScript();
        if (!window.Razorpay) {
          setError('Payment could not be initialized.');
          setBooking(false);
          return;
        }
        setBooking(false);
        const rzp = new window.Razorpay({
          key: cd.key_id,
          amount: cd.amount,
          currency: cd.currency || 'INR',
          order_id: cd.razorpay_order_id,
          name: hotelName || 'QRoom',
          description: `Amenity: ${amenity?.name || 'Booking'} (prepaid)`,
          handler: async (response) => {
            setBooking(true);
            setError('');
            try {
              await submitAmenityBooking({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              });
            } catch (err: unknown) {
              setError(err instanceof Error ? err.message : 'Booking failed after payment.');
            } finally {
              setBooking(false);
            }
          },
          modal: {
            ondismiss: () => setBooking(false),
          },
          theme: { color: '#4f46e5' },
        });
        rzp.open();
        return;
      }

      await submitAmenityBooking();
      setBooking(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Network error. Please try again.');
      setBooking(false);
    }
  };

  // Get today's date in yyyy-mm-dd
  const today = new Date();
  const minDate = today.toISOString().slice(0, 10);

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Book {amenity?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4"><span className="spinner-border" /> Loading slots...</div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : success ? (
          <div className="alert alert-success text-center py-4">
            <i className="fas fa-check-circle fa-2x mb-2 text-success"></i><br />
            {successMsg}
          </div>
        ) : (
          <>
            <div className="mb-3">
              <strong>Select a date:</strong>
              <input
                type="date"
                className="form-control mt-2"
                min={minDate}
                value={selectedDate}
                onChange={e => {
                  setSelectedDate(e.target.value);
                  setSelectedSlot(null);
                }}
                disabled={booking}
                onKeyDown={e => e.preventDefault()}
                style={{ cursor: 'pointer', backgroundColor: '#fff' }}
              />
            </div>
            {selectedDate && (
              <div className="mb-3">
                <strong>Available slots for {selectedDate}:</strong>
                <div className="list-group mt-2">
                  {filteredSlots.length === 0 && <div className="text-muted">No slots available for this date.</div>}
                  {filteredSlots.map(slot => (
                    <button
                      key={slot.id}
                      className={`list-group-item list-group-item-action${selectedSlot && selectedSlot.id === slot.id ? ' active' : ''}`}
                      onClick={() => setSelectedSlot(slot)}
                      disabled={booking}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <span><i className="fas fa-clock me-2"></i>{slot.start_time} - {slot.end_time}</span>
                        <span className="badge bg-primary">₹{slot.price}</span>
                      </div>
                      <div className="small text-muted">Capacity: {slot.slot_capacity}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">Special Request (optional)</label>
              <input
                type="text"
                className="form-control"
                value={specialRequest}
                onChange={e => setSpecialRequest(e.target.value)}
                disabled={booking}
                maxLength={200}
                placeholder="e.g. Need wheelchair access, etc."
              />
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!success && (
          <Button variant="secondary" onClick={onHide} disabled={booking}>Cancel</Button>
        )}
        {!success && (
          <Button
            variant="primary"
            onClick={handleBook}
            disabled={!selectedSlot || !selectedDate || booking || !paymentsConfigLoaded}
          >
            <i className="fas fa-calendar-check me-2" />
            {amenitiesPrepaidRequired ? 'Book now (prepaid)' : 'Book Now'}
          </Button>
        )}
      </Modal.Footer>
      {selectedSlot && (
        <BookingConfirmationModal
          show={showConfirm}
          onHide={() => setShowConfirm(false)}
          onConfirm={handleConfirmBooking}
          prepaidRequired={amenitiesPrepaidRequired}
          bookingDetails={{
            amenityName: amenity?.name,
            date: selectedDate,
            slot: selectedSlot,
            price: selectedSlot?.price,
            specialRequest: specialRequest,
          }}
        />
      )}
    </Modal>
  );
};

export default AmenitiesBookingModal; 